import KarenOrbButton from "../components/KarenOrb";
/**
 * Universal Intent · Conversation Surface.
 *
 * Governed by:
 *   BLUEPRINT_UNIVERSAL_INTENT.md R3 · §§1A, 1B, 1C
 *   GC60 Programme Direction · Priority 3
 *
 * This is the single conversational front door. Users type OR speak an
 * intent. The Orchestrator classifies, dispatches, and narrates back —
 * in ONE consistent voice. Mode-switch (text ↔ voice) is silent (U-0d).
 *
 * The page renders three things:
 *   1) A transcript of the conversation so far.
 *   2) A composer (text field + voice-record button).
 *   3) When the Orchestrator returns action=offer_navigation, an inline
 *      "Take me there" button that navigates to the specialist surface
 *      (e.g. /import-todays-recordings).
 *
 * Every user-visible string EXCEPT the transcript's own user-typed messages
 * comes from the backend narrator — this component MUST NOT invent
 * narration.
 */
import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;
const OPTS = { withCredentials: true };

export default function TalkToNDG() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [sessionId, setSessionId] = useState(null);
  const [transcript, setTranscript] = useState([]); // [{role, text, action?, dispatch_result?}]
  const [composer, setComposer] = useState("");
  const [pending, setPending] = useState(false);
  const [recordSupported, setRecordSupported] = useState(false);
  const [recording, setRecording] = useState(false);
  const [pendingNavigation, setPendingNavigation] = useState(null); // {target, reason}
  const mediaRecorderRef = useRef(null);
  const microphoneStreamRef = useRef(null);
  const stopWatchdogRef = useRef(null);
  const chunkBufferRef = useRef([]);
  const transcriptEndRef = useRef(null);
  const initialQueryHandledRef = useRef(false);

  // Open a session on mount.
  useEffect(() => {
    (async () => {
      try {
        const res = await axios.post(`${API}/api/universal_intent/session`, {}, OPTS);
        setSessionId(res.data.session_id);
        setTranscript([{ role: "ndg", text: res.data.narration }]);
      } catch (err) {
        setTranscript([{
          role: "ndg",
          text: "I couldn't open our conversation just now. Please refresh and try again.",
        }]);
      }
    })();
  }, []);

  // Feature detect microphone support.
  useEffect(() => {
    setRecordSupported(
      typeof navigator !== "undefined"
        && navigator.mediaDevices
        && typeof navigator.mediaDevices.getUserMedia === "function"
        && typeof window.MediaRecorder !== "undefined",
    );
  }, []);

  const releaseMicrophone = useCallback(() => {
    if (stopWatchdogRef.current) {
      clearTimeout(stopWatchdogRef.current);
      stopWatchdogRef.current = null;
    }
    microphoneStreamRef.current?.getTracks().forEach((track) => track.stop());
    microphoneStreamRef.current = null;
    mediaRecorderRef.current = null;
    setRecording(false);
  }, []);

  useEffect(() => releaseMicrophone, [releaseMicrophone]);

  // Auto-scroll on new turn.
  useEffect(() => {
    if (transcriptEndRef.current) {
      transcriptEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [transcript]);

  const applyEnvelope = useCallback((env) => {
    setTranscript((prev) => [...prev, {
      role: "ndg",
      text: env.narration,
      action: env.action,
      dispatch_result: env.dispatch_result,
    }]);
    if (env.action === "offer_navigation" && env.dispatch_result && env.dispatch_result.navigation_target) {
      setPendingNavigation({
        target: env.dispatch_result.navigation_target,
        reason: env.dispatch_result.navigation_reason,
      });
    }
  }, []);

  const sendText = useCallback(async () => {
    const text = composer.trim();
    if (!text || !sessionId) return;
    setTranscript((prev) => [...prev, { role: "user", text, mode: "text" }]);
    setComposer("");
    setPending(true);
    try {
      const res = await axios.post(
        `${API}/api/universal_intent/session/${sessionId}/turn`,
        { text, input_mode: "text" },
        OPTS,
      );
      applyEnvelope(res.data);
    } catch (err) {
      setTranscript((prev) => [...prev, {
        role: "ndg",
        text: "I couldn't process that just now — could you try again?",
      }]);
    } finally {
      setPending(false);
    }
  }, [composer, sessionId, applyEnvelope]);

  // GC64 · MP-12 · Feb 2026 — accept a `?q=<utterance>` handoff
  // from the Toolbox search's no-confident-match fallback. When
  // the session opens and a `q` param is present, seed the
  // composer AND auto-send it so the user's original request is
  // never dropped at the boundary between Toolbox and assistant.
  useEffect(() => {
    if (!sessionId || initialQueryHandledRef.current) return;
    const initial = (searchParams.get("q") || "").trim();
    if (!initial) return;
    initialQueryHandledRef.current = true;
    // Seed the composer so the user sees what got sent, and
    // trigger a send on the next tick (after composer state
    // update lands and sendText picks it up).
    setComposer(initial);
    setTimeout(() => {
      // Directly issue the turn — avoids waiting on composer state
      // for the initial handoff (state batching could otherwise
      // leave `composer` empty when sendText reads it).
      (async () => {
        setTranscript((prev) => [...prev, { role: "user", text: initial, mode: "text" }]);
        setComposer("");
        setPending(true);
        try {
          const res = await axios.post(
            `${API}/api/universal_intent/session/${sessionId}/turn`,
            { text: initial, input_mode: "text" },
            OPTS,
          );
          applyEnvelope(res.data);
        } catch {
          setTranscript((prev) => [...prev, {
            role: "ndg",
            text: "I couldn't process that just now — could you try again?",
          }]);
        } finally {
          setPending(false);
        }
      })();
    }, 0);
  }, [sessionId, searchParams, applyEnvelope]);

  const startRecording = useCallback(async () => {
    if (!recordSupported || !sessionId || pending) return;
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== "inactive") return;

    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        },
      });
      microphoneStreamRef.current = stream;

      // Safari/iPhone records MP4/AAC; Chromium generally records WebM/Opus.
      const candidates = [
        "audio/webm;codecs=opus",
        "audio/webm",
        "audio/mp4;codecs=mp4a.40.2",
        "audio/mp4",
      ];
      const mimeType = typeof MediaRecorder.isTypeSupported === "function"
        ? candidates.find((type) => MediaRecorder.isTypeSupported(type))
        : null;
      const mr = mimeType
        ? new MediaRecorder(stream, { mimeType })
        : new MediaRecorder(stream);
      const actualType = mr.mimeType || mimeType || "audio/webm";
      const extension = actualType.includes("mp4") ? "m4a"
        : actualType.includes("ogg") ? "ogg"
        : "webm";

      chunkBufferRef.current = [];
      mediaRecorderRef.current = mr;
      mr.ondataavailable = (event) => {
        if (event.data?.size) chunkBufferRef.current.push(event.data);
      };
      mr.onerror = () => {
        releaseMicrophone();
        setTranscript((prev) => [...prev, {
          role: "ndg",
          text: "The microphone stopped unexpectedly. Tap the blue circle to try again.",
        }]);
      };
      mr.onstop = async () => {
        const blob = new Blob(chunkBufferRef.current, { type: actualType });
        releaseMicrophone();

        if (!blob.size) {
          setTranscript((prev) => [...prev, {
            role: "ndg",
            text: "I didn't receive any sound. Tap the blue circle and try again.",
          }]);
          return;
        }

        setPending(true);
        setTranscript((prev) => [...prev, { role: "user", text: "🎙 (voice)", mode: "voice" }]);
        try {
          const form = new FormData();
          form.append("audio", blob, `turn.${extension}`);
          const res = await axios.post(
            `${API}/api/universal_intent/session/${sessionId}/turn/voice`,
            form,
            OPTS,
          );
          setTranscript((prev) => {
            const next = [...prev];
            const lastUser = [...next].reverse().find((row) => row.role === "user" && row.mode === "voice");
            if (lastUser) lastUser.text = `🎙 ${res.data.input_text}`;
            return next;
          });
          applyEnvelope(res.data);
        } catch (err) {
          setTranscript((prev) => [...prev, {
            role: "ndg",
            text: "I couldn't understand the recording — could you try again or type instead?",
          }]);
        } finally {
          setPending(false);
        }
      };

      mr.start(250);
      setRecording(true);
    } catch (err) {
      releaseMicrophone();
      const denied = err?.name === "NotAllowedError" || err?.name === "SecurityError";
      setTranscript((prev) => [...prev, {
        role: "ndg",
        text: denied
          ? "Microphone access is blocked. Allow it in your browser settings, then tap the blue circle."
          : "I couldn't reach the microphone — you can try again or type instead.",
      }]);
    }
  }, [recordSupported, sessionId, pending, applyEnvelope, releaseMicrophone]);

  const stopRecording = useCallback(() => {
    const recorder = mediaRecorderRef.current;
    if (!recorder || recorder.state === "inactive") {
      releaseMicrophone();
      return;
    }

    setRecording(false);
    try {
      recorder.requestData();
    } catch { /* Safari may reject requestData while it is finalising. */ }
    recorder.stop();

    stopWatchdogRef.current = setTimeout(() => {
      if (mediaRecorderRef.current) {
        releaseMicrophone();
        setPending(false);
        setTranscript((prev) => [...prev, {
          role: "ndg",
          text: "The recording didn't finish properly. Tap the blue circle to try again.",
        }]);
      }
    }, 5000);
  }, [releaseMicrophone]);

  const acceptNavigation = useCallback(() => {
    if (pendingNavigation && pendingNavigation.target) {
      navigate(pendingNavigation.target);
    }
    setPendingNavigation(null);
  }, [navigate, pendingNavigation]);

  const dismissNavigation = useCallback(() => setPendingNavigation(null), []);

  return (
    <main data-testid="ui-orchestrator-page" className="ui-talk-page">
      <header className="ui-talk-header">
        <Link to="/" data-testid="ui-talk-back-link" className="ui-talk-back">← Home</Link>
        <h1 data-testid="ui-talk-title">Talk to NDG</h1>
        <p className="ui-talk-hint">Type or speak — either is right.</p>
      </header>

      <section
        className="ui-talk-transcript"
        role="log"
        aria-live="polite"
        data-testid="ui-talk-transcript"
      >
        {transcript.map((turn, i) => (
          <div
            key={i}
            className={`ui-talk-turn ui-talk-turn--${turn.role}`}
            data-testid={`ui-talk-turn-${i}`}
          >
            <div className="ui-talk-bubble" data-testid={`ui-talk-bubble-${turn.role}-${i}`}>
              {turn.text}
            </div>
          </div>
        ))}
        <div ref={transcriptEndRef} />
      </section>

      {pendingNavigation && (
        <section className="ui-talk-nav-offer" data-testid="ui-talk-nav-offer">
          <button
            type="button"
            onClick={acceptNavigation}
            className="ui-talk-cta ui-talk-cta--primary"
            data-testid="ui-talk-nav-accept-btn"
          >
            Take me there
          </button>
          <button
            type="button"
            onClick={dismissNavigation}
            className="ui-talk-cta ui-talk-cta--ghost"
            data-testid="ui-talk-nav-dismiss-btn"
          >
            Not right now
          </button>
        </section>
      )}

      <form
        className="ui-talk-composer"
        onSubmit={(e) => { e.preventDefault(); sendText(); }}
        data-testid="ui-talk-composer"
      >
        <input
          type="text"
          value={composer}
          onChange={(e) => setComposer(e.target.value)}
          disabled={pending || !sessionId}
          placeholder="Tell me what you'd like to do…"
          className="ui-talk-input"
          data-testid="ui-talk-input"
          aria-label="Message"
        />
        <button
          type="submit"
          disabled={pending || !sessionId || !composer.trim()}
          className="ui-talk-cta"
          data-testid="ui-talk-send-btn"
        >
          Send
        </button>
        {recordSupported && (
          <KarenOrbButton
            onClick={recording ? stopRecording : startRecording}
            disabled={pending || !sessionId}
            active={recording}
            data-testid="ui-talk-voice-btn"
            label={recording ? "Stop recording" : "Speak to Karen"}
          />
        )}
      </form>
    </main>
  );
}
